import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { login } from "@/api/auth";
import { apiErrorText } from "@/lib/apiError";
import { useAuthStore } from "@/stores/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { AuthLanguagePicker } from "@/components/AuthLanguagePicker";
import { SUPPORTED_LANGUAGES, type AppLanguage } from "@/i18n";

const DEMO_PASSWORD = "demo";
// One demo account per language: the seeded content — session titles, the coach
// narrative, the chat — lives in the rows, and those tables have no translation
// columns. `?demo=de` therefore has to pick a different account, not merely a
// different UI language. Unknown values fall back to the English one.
function supportedLanguage(value: string | null): AppLanguage {
  return value !== null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
    ? value as AppLanguage : "en";
}
function demoEmail(param: string | null): string {
  const language = supportedLanguage(param);
  return language !== "en"
    ? `demo-${language}@mavinty.com`
    : "demo@mavinty.com";
}

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const demoParam = params.get("demo");
  const isDemo = demoParam !== null;
  const DEMO_EMAIL = demoEmail(demoParam);
  const requestedLanguage = params.has("lang") ? supportedLanguage(params.get("lang"))
    : isDemo ? supportedLanguage(demoParam) : null;
  useEffect(() => {
    // Apply the arriving link once per requested language. Do not depend on
    // i18n.language: a later explicit picker choice must not be reset by the URL.
    // With no language hint, retain the existing browser/picker preference.
    if (requestedLanguage !== null) void i18n.changeLanguage(requestedLanguage);
  }, [i18n, requestedLanguage]);
  // Prefilled from the query flag so the visitor lands on a filled form and can
  // press one button. Read from the initialiser rather than an effect, so the
  // inputs are never briefly empty.
  const [email, setEmail] = useState(isDemo ? DEMO_EMAIL : "");
  const [password, setPassword] = useState(isDemo ? DEMO_PASSWORD : "");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const setAuth = useAuthStore((s) => s.setAuth);
  const nav = useNavigate();
  const resetSuccess = params.get("reset") === "success";
  const pwChanged = params.get("pw") === "changed";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const token = await login(email, password);
      setAuth(token, email);
      nav("/", { replace: true });
    } catch (err) {
      setError(apiErrorText(err, t("auth.login_error")));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <AuthLanguagePicker />
      <div className="mb-8 text-center">
        <h1 className="font-serif text-[34px] font-medium tracking-tight">{t("app.title")}</h1>
        <p className="mt-2 font-serif text-[15px] italic text-taupe">
          {t("app.tagline")}
        </p>
      </div>

      {resetSuccess || pwChanged ? (
        <div className="mb-4 rounded-lg border border-amber/40 bg-amber/10 px-4 py-3 text-[13px] text-navy-deep">
          {t("auth.password_changed_hint")}
        </div>
      ) : null}

      <Card className="space-y-4">
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            type="email"
            label={t("auth.email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          <Input
            type="password"
            label={t("auth.password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
          {error ? (
            <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {error}
            </div>
          ) : null}
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            {loading ? t("auth.signing_in") : t("auth.sign_in")}
          </Button>
        </form>

        {!isDemo && (
          <button
            type="button"
            onClick={() => {
              setEmail(demoEmail(i18n.language));
              setPassword(DEMO_PASSWORD);
            }}
            className="mb-4 w-full rounded border border-dashed border-cream-border py-2 text-[13px] text-taupe hover:border-navy hover:text-navy focus:outline-none focus:ring-2 focus:ring-amber/45"
          >
            {t("auth.try_demo", "Look around a demo account first →")}
          </button>
        )}
        <div className="flex items-center justify-between border-t border-cream-border pt-4 text-[13px]">
          <Link
            to="/forgot-password"
            className="font-medium text-amber-dark hover:underline"
          >
            {t("auth.forgot_password")}
          </Link>
          <Link to={`/register?lang=${supportedLanguage(i18n.language)}`} className="text-taupe hover:text-navy">
            {t("auth.register")}
          </Link>
        </div>
      </Card>
    </div>
  );
}
